import React from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useHeader from 'containers/App/Header/useHeader';
import { RootState } from 'types';
import {
  useGetUserFeedbackJiraConfigQuery,
  useGetUserFeedbackQuery,
  useSyncFeedbackToJiraMutation,
  useUpdateUserFeedbackJiraConfigMutation,
  useUpdateUserFeedbackStatusMutation,
} from 'services/api/userFeedback';

import { FeedbackManagementPageStyles } from '../components/style';
import {
  UpdateUserFeedbackJiraConfigRequest,
  UserFeedbackRecord,
  UserFeedbackStatus,
} from '../types';
import {
  formatFeedbackDateTime,
  getFeedbackCategoryVariant,
  getFeedbackErrorMessage,
  getFeedbackStatusVariant,
  mapSessionUserToFeedbackActor,
} from '../utils';

type FilterValue = 'all' | UserFeedbackStatus;
type FeedbackBadgeVariant =
  | 'danger'
  | 'dark'
  | 'info'
  | 'light'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning';

const FILTER_OPTIONS: Array<{ label: string; value: FilterValue }> = [
  { label: 'Open', value: 'open' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'All', value: 'all' },
];
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 10;

const normalizeSearchValue = (value?: string | null) =>
  String(value || '').trim().toLowerCase();

const feedbackMatchesSearch = (
  feedback: UserFeedbackRecord,
  searchTerm: string,
) => {
  const normalizedSearch = normalizeSearchValue(searchTerm);
  if (!normalizedSearch) return true;

  return [
    feedback.summary,
    feedback.details,
    feedback.category,
    feedback.status,
    feedback.page.label,
    feedback.page.path,
    feedback.page.search,
    feedback.page.podName,
    feedback.reporter.name,
    feedback.reporter.email,
    feedback.jira?.issueKey,
    feedback.jira?.issueUrl,
    feedback.jira?.status,
    feedback.jira?.issueStatus,
    feedback.jira?.lastError,
  ]
    .map(value => normalizeSearchValue(value))
    .join(' ')
    .includes(normalizedSearch);
};

const formatFeedbackSubmittedParts = (value?: string | null) => {
  if (!value) return { date: 'Not available', time: '' };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: value, time: '' };

  const dateText = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const timeText = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);

  return { date: dateText, time: timeText };
};

export default function UserFeedbackManagementPage() {
  useHeader('User Feedback');

  const sessionUser = useSelector((state: RootState) => state.session.user);
  const currentActor = mapSessionUserToFeedbackActor(sessionUser);

  const [filter, setFilter] = React.useState<FilterValue>('open');
  const [selectedFeedbackId, setSelectedFeedbackId] =
    React.useState<string | null>(null);
  const [searchText, setSearchText] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [jiraDraft, setJiraDraft] =
    React.useState<UpdateUserFeedbackJiraConfigRequest>({
      enabled: false,
      adminEmail: '',
      apiToken: '',
      masterEpicKey: '',
    });

  const { data = [], error, isFetching, isLoading } = useGetUserFeedbackQuery(
    undefined,
    { pollingInterval: 15000 },
  );
  const {
    data: jiraConfig,
    error: jiraConfigError,
    isFetching: isJiraConfigFetching,
  } = useGetUserFeedbackJiraConfigQuery();
  const [updateUserFeedbackStatus, updateStatusState] =
    useUpdateUserFeedbackStatusMutation();
  const [updateUserFeedbackJiraConfig, updateJiraConfigState] =
    useUpdateUserFeedbackJiraConfigMutation();
  const [syncFeedbackToJira, syncJiraState] = useSyncFeedbackToJiraMutation();

  const filteredFeedback = React.useMemo(() => {
    const sorted = [...data].sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    );

    const statusFiltered =
      filter === 'all' ? sorted : sorted.filter(item => item.status === filter);

    return statusFiltered.filter(item => feedbackMatchesSearch(item, searchText));
  }, [data, filter, searchText]);

  const selectedFeedback = React.useMemo(
    () =>
      filteredFeedback.find(item => item.id === selectedFeedbackId) ||
      filteredFeedback[0] ||
      null,
    [filteredFeedback, selectedFeedbackId],
  );

  const totalPages = React.useMemo(
    () => Math.max(1, Math.ceil(filteredFeedback.length / pageSize)),
    [filteredFeedback.length, pageSize],
  );

  const paginatedFeedback = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredFeedback.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredFeedback, pageSize]);

  const paginationStart = filteredFeedback.length
    ? (currentPage - 1) * pageSize + 1
    : 0;
  const paginationEnd = Math.min(currentPage * pageSize, filteredFeedback.length);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, pageSize, searchText]);

  React.useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  React.useEffect(() => {
    if (!filteredFeedback.length) {
      setSelectedFeedbackId(null);
      return;
    }

    if (!selectedFeedbackId) {
      setSelectedFeedbackId(filteredFeedback[0].id);
      return;
    }

    if (!filteredFeedback.some(item => item.id === selectedFeedbackId)) {
      setSelectedFeedbackId(filteredFeedback[0].id);
    }
  }, [filteredFeedback, selectedFeedbackId]);

  React.useEffect(() => {
    if (!paginatedFeedback.length) return;

    if (
      !selectedFeedbackId ||
      !paginatedFeedback.some(item => item.id === selectedFeedbackId)
    ) {
      setSelectedFeedbackId(paginatedFeedback[0].id);
    }
  }, [paginatedFeedback, selectedFeedbackId]);

  React.useEffect(() => {
    if (!jiraConfig) return;

    setJiraDraft({
      enabled: jiraConfig.enabled,
      adminEmail: jiraConfig.adminEmail || '',
      apiToken: '',
      masterEpicKey: jiraConfig.masterEpicKey || '',
    });
  }, [jiraConfig]);

  const counts = React.useMemo(
    () => ({
      all: data.length,
      open: data.filter(item => item.status === 'open').length,
      resolved: data.filter(item => item.status === 'resolved').length,
    }),
    [data],
  );

  const jiraConfigStatus = React.useMemo<{
    label: string;
    variant: FeedbackBadgeVariant;
  }>(() => {
    if (isJiraConfigFetching && !jiraConfig) {
      return { label: 'Loading', variant: 'secondary' };
    }

    if (!jiraConfig?.enabled) {
      return { label: 'Disabled', variant: 'secondary' };
    }

    if (
      jiraConfig.siteUrl &&
      jiraConfig.adminEmail &&
      jiraConfig.hasApiToken &&
      jiraConfig.masterEpicKey &&
      jiraConfig.projectKey
    ) {
      return { label: 'Ready', variant: 'success' };
    }

    return { label: 'Needs setup', variant: 'warning' };
  }, [isJiraConfigFetching, jiraConfig]);

  const handleStatusChange = async (
    feedback: UserFeedbackRecord,
    status: UserFeedbackStatus,
  ) => {
    try {
      await updateUserFeedbackStatus({
        feedbackId: feedback.id,
        status,
      }).unwrap();
      toast.success(
        status === 'resolved'
          ? 'Feedback marked as resolved.'
          : 'Feedback reopened.',
      );
    } catch (mutationError) {
      toast.error(
        getFeedbackErrorMessage(
          mutationError,
          'Unable to update feedback status right now.',
        ),
      );
    }
  };

  const handleSaveJiraConfig = async () => {
    try {
      await updateUserFeedbackJiraConfig({
        ...jiraDraft,
        apiToken: jiraDraft.apiToken?.trim() || undefined,
      }).unwrap();
      toast.success('Jira configuration saved.');
    } catch (mutationError) {
      toast.error(
        getFeedbackErrorMessage(
          mutationError,
          'Unable to save Jira configuration right now.',
        ),
      );
    }
  };

  const handleSyncJira = async (feedback: UserFeedbackRecord) => {
    try {
      const updatedFeedback = await syncFeedbackToJira({
        feedbackId: feedback.id,
      }).unwrap();

      if (updatedFeedback.jira?.status === 'linked') {
        toast.success(
          updatedFeedback.jira.issueKey
            ? `Linked to Jira ${updatedFeedback.jira.issueKey}.`
            : 'Feedback linked to Jira.',
        );
        return;
      }

      toast.warn(
        updatedFeedback.jira?.lastError ||
          'Jira sync did not complete. Review the integration settings.',
      );
    } catch (mutationError) {
      toast.error(
        getFeedbackErrorMessage(
          mutationError,
          'Unable to sync this feedback to Jira right now.',
        ),
      );
    }
  };

  return (
    <FeedbackManagementPageStyles>
      <div className="feedback-admin-shell">
        <div className="feedback-admin-header">
          <div>
            <div className="feedback-admin-eyebrow">Admin Center</div>
            <h1 className="feedback-admin-title">User Feedback</h1>
            <p className="feedback-admin-subtitle">
              Review submitted feedback, verify Jira links, and close the portal
              loop after Jira work is resolved.
            </p>
          </div>

          <div className="feedback-metric-grid">
            <div className="feedback-metric-card">
              <p className="feedback-metric-label">Open</p>
              <p className="feedback-metric-value">{counts.open}</p>
            </div>
            <div className="feedback-metric-card">
              <p className="feedback-metric-label">Resolved</p>
              <p className="feedback-metric-value">{counts.resolved}</p>
            </div>
            <div className="feedback-metric-card">
              <p className="feedback-metric-label">Total</p>
              <p className="feedback-metric-value">{counts.all}</p>
            </div>
          </div>
        </div>

        <section className="feedback-panel feedback-jira-config-card">
          <div className="feedback-jira-config-header">
            <div>
              <h2 className="feedback-panel-title">Jira integration</h2>
              <p className="feedback-admin-subtitle">
                New issues become Jira Bugs and general feedback becomes Jira
                Tasks under the configured Master Epic in Alphonso Jira.
              </p>
            </div>
            <Badge bg={jiraConfigStatus.variant}>
              {jiraConfigStatus.label}
            </Badge>
          </div>

          {jiraConfigError ? (
            <Alert variant="danger">
              {getFeedbackErrorMessage(
                jiraConfigError,
                'Failed to load Jira configuration right now.',
              )}
            </Alert>
          ) : null}

          <div className="feedback-jira-config-grid">
            <Form.Group className="feedback-jira-switch">
              <Form.Check
                checked={jiraDraft.enabled}
                id="feedback-jira-enabled"
                label="Create Jira tickets for new submissions"
                onChange={event =>
                  setJiraDraft(current => ({
                    ...current,
                    enabled: event.target.checked,
                  }))
                }
                type="switch"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Jira site URL</Form.Label>
              <Form.Control
                readOnly
                value={jiraConfig?.siteUrl || 'https://alphonsotv.atlassian.net'}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Admin email</Form.Label>
              <Form.Control
                onChange={event =>
                  setJiraDraft(current => ({
                    ...current,
                    adminEmail: event.target.value,
                  }))
                }
                placeholder="jira-admin@lgads.tv"
                value={jiraDraft.adminEmail}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Admin API token</Form.Label>
              <Form.Control
                onChange={event =>
                  setJiraDraft(current => ({
                    ...current,
                    apiToken: event.target.value,
                  }))
                }
                placeholder={
                  jiraConfig?.hasApiToken
                    ? 'Saved token is active - enter a new token to replace it'
                    : 'Paste Jira API token'
                }
                type="password"
                value={jiraDraft.apiToken || ''}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Master Epic key</Form.Label>
              <Form.Control
                onChange={event =>
                  setJiraDraft(current => ({
                    ...current,
                    masterEpicKey: event.target.value,
                  }))
                }
                placeholder="TVLAB-123"
                value={jiraDraft.masterEpicKey}
              />
            </Form.Group>
          </div>

          <div className="feedback-jira-config-footer">
            <span>
              Hardcoded mapping: issues to Bug, feedback to Task. Project key is
              derived from the Master Epic key. Tokens are encrypted before
              storage when the server token secret is configured.
            </span>
            {jiraConfig?.tokenProtected ? (
              <Badge bg="success">Token protected</Badge>
            ) : (
              <Badge bg="warning">Token secret needed</Badge>
            )}
            <Button
              disabled={updateJiraConfigState.isLoading}
              onClick={handleSaveJiraConfig}
              variant="primary"
            >
              {updateJiraConfigState.isLoading ? 'Saving...' : 'Save Jira settings'}
            </Button>
          </div>
        </section>

        <div className="feedback-toolbar">
          <div className="feedback-filter-group">
            {FILTER_OPTIONS.map(option => (
              <Button
                key={option.value}
                onClick={() => setFilter(option.value)}
                variant={filter === option.value ? 'primary' : 'light'}
              >
                {option.label}
              </Button>
            ))}
          </div>

          <Form.Control
            className="feedback-admin-search"
            onChange={event => setSearchText(event.target.value)}
            placeholder="Search feedback, pages, people, or Jira IDs"
            value={searchText}
          />

          <div className="text-muted small">
            Signed in as {currentActor.name}
          </div>
        </div>

        {error ? (
          <Alert variant="danger">
            {getFeedbackErrorMessage(
              error,
              'Failed to load feedback records right now.',
            )}
          </Alert>
        ) : null}

        <div className="feedback-grid">
          <section className="feedback-panel feedback-list-panel">
            <h2 className="feedback-panel-title">Submitted feedback</h2>

            {isLoading || isFetching ? (
              <div className="feedback-empty-state">
                <div>
                  <Spinner animation="border" className="mb-3" />
                  <div>Loading feedback records...</div>
                </div>
              </div>
            ) : filteredFeedback.length ? (
              <div className="feedback-table-wrap">
                <Table className="feedback-table" hover responsive>
                  <thead>
                    <tr>
                      <th className="feedback-status-column">Status</th>
                      <th className="feedback-issue-column">Issue</th>
                      <th className="feedback-ticket-column">Ticket ID</th>
                      <th className="feedback-linked-column">Linked</th>
                      <th>Page</th>
                      <th>Reporter</th>
                      <th>Submitted at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFeedback.map(item => {
                      const submittedAt = formatFeedbackSubmittedParts(
                        item.createdAt,
                      );

                      return (
                        <tr
                          key={item.id}
                          className={
                            item.id === selectedFeedback?.id ? 'is-selected' : ''
                          }
                          onClick={() => setSelectedFeedbackId(item.id)}
                        >
                          <td>
                            <Badge bg={getFeedbackStatusVariant(item.status)}>
                              {item.status}
                            </Badge>
                          </td>
                          <td className="feedback-summary-cell">
                            <p className="feedback-summary-title">
                              {item.summary}
                            </p>
                            <p className="feedback-summary-meta">
                              <Badge
                                bg={getFeedbackCategoryVariant(item.category)}
                              >
                                {item.category}
                              </Badge>
                            </p>
                          </td>
                          <td className="feedback-ticket-id-cell">
                            {item.jira?.issueKey ? (
                              <a
                                className="feedback-jira-inline-link"
                                href={item.jira.issueUrl}
                                onClick={event => event.stopPropagation()}
                                rel="noreferrer"
                                target="_blank"
                              >
                                {item.jira.issueKey}
                              </a>
                            ) : (
                              <span className="feedback-muted">Pending</span>
                            )}
                          </td>
                          <td className="feedback-linked-cell">
                            <Badge
                              bg={item.jira?.issueKey ? 'success' : 'secondary'}
                            >
                              {item.jira?.issueKey ? 'Linked' : 'Not linked'}
                            </Badge>
                          </td>
                          <td>{item.page.label}</td>
                          <td>{item.reporter.name}</td>
                          <td className="feedback-submitted-cell">
                            <span>{submittedAt.date}</span>
                            {submittedAt.time ? <span>{submittedAt.time}</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <div className="feedback-table-footer">
                  <span>
                    Showing {paginationStart}-{paginationEnd} of{' '}
                    {filteredFeedback.length}
                  </span>
                  <div className="feedback-pagination-controls">
                    <Form.Control
                      as="select"
                      onChange={event =>
                        setPageSize(Number(event.target.value) || DEFAULT_PAGE_SIZE)
                      }
                      size="sm"
                      value={pageSize}
                    >
                      {PAGE_SIZE_OPTIONS.map(option => (
                        <option key={option} value={option}>
                          {option} / page
                        </option>
                      ))}
                    </Form.Control>
                    <Button
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage(page => Math.max(1, page - 1))
                      }
                      size="sm"
                      variant="light"
                    >
                      Previous
                    </Button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage(page => Math.min(totalPages, page + 1))
                      }
                      size="sm"
                      variant="light"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="feedback-empty-state">
                No feedback records match the current filter.
              </div>
            )}
          </section>

          <aside className="feedback-panel feedback-detail-panel">
            <h2 className="feedback-panel-title">Feedback details</h2>

            {selectedFeedback ? (
              <>
                <div className="feedback-detail-header">
                  <div>
                    <h3 className="feedback-detail-title">
                      {selectedFeedback.summary}
                    </h3>
                    <div className="mt-2">
                      <Badge
                        bg={getFeedbackCategoryVariant(selectedFeedback.category)}
                        className="mr-2"
                      >
                        {selectedFeedback.category}
                      </Badge>
                      <Badge bg={getFeedbackStatusVariant(selectedFeedback.status)}>
                        {selectedFeedback.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="feedback-detail-actions">
                    <Button
                      disabled={
                        syncJiraState.isLoading ||
                        !jiraConfig?.enabled ||
                        selectedFeedback.jira?.status === 'linked'
                      }
                      onClick={() => handleSyncJira(selectedFeedback)}
                      variant="outline-primary"
                    >
                      {selectedFeedback.jira?.status === 'linked'
                        ? 'Jira linked'
                        : 'Sync Jira'}
                    </Button>
                    <Button
                      disabled={updateStatusState.isLoading}
                      onClick={() =>
                        handleStatusChange(
                          selectedFeedback,
                          selectedFeedback.status === 'resolved'
                            ? 'open'
                            : 'resolved',
                        )
                      }
                      variant={
                        selectedFeedback.status === 'resolved'
                          ? 'outline-secondary'
                          : 'success'
                      }
                    >
                      {selectedFeedback.status === 'resolved'
                        ? 'Reopen'
                        : 'Mark resolved'}
                    </Button>
                  </div>
                </div>

                <div className="feedback-detail-meta">
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Reporter</p>
                    <p className="feedback-meta-value">
                      {selectedFeedback.reporter.name}
                      {selectedFeedback.reporter.email
                        ? ` (${selectedFeedback.reporter.email})`
                        : ''}
                    </p>
                  </div>
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Submitted</p>
                    <p className="feedback-meta-value">
                      {formatFeedbackDateTime(selectedFeedback.createdAt)}
                    </p>
                  </div>
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Page</p>
                    <p className="feedback-meta-value">
                      {selectedFeedback.page.label}
                    </p>
                  </div>
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Path</p>
                    <p className="feedback-meta-value">
                      {selectedFeedback.page.path}
                      {selectedFeedback.page.search || ''}
                    </p>
                  </div>
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Resolved by</p>
                    <p className="feedback-meta-value">
                      {selectedFeedback.resolvedBy?.name || 'Not resolved yet'}
                    </p>
                  </div>
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Resolved at</p>
                    <p className="feedback-meta-value">
                      {selectedFeedback.resolvedAt
                        ? formatFeedbackDateTime(selectedFeedback.resolvedAt)
                        : 'Not resolved yet'}
                    </p>
                  </div>
                  <div className="feedback-meta-card">
                    <p className="feedback-meta-label">Ticket ID</p>
                    <p className="feedback-meta-value">
                      {selectedFeedback.jira?.issueKey ? (
                        <a
                          href={selectedFeedback.jira.issueUrl}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {selectedFeedback.jira.issueKey}
                        </a>
                      ) : (
                        'Pending'
                      )}
                    </p>
                  </div>
                </div>

                <div className="feedback-description">
                  {selectedFeedback.details}
                </div>

                <div className="feedback-jira-admin-note">
                  <div>
                    <h3 className="feedback-panel-title">Jira discussion</h3>
                    <p>
                      Comments and investigation updates should be handled on the
                      Jira ticket so there is one source of truth.
                    </p>
                  </div>
                  {selectedFeedback.jira?.issueUrl ? (
                    <Button
                      as="a"
                      href={selectedFeedback.jira.issueUrl}
                      rel="noreferrer"
                      target="_blank"
                      variant="primary"
                    >
                      <span>Open</span>
                      <span>{selectedFeedback.jira.issueKey}</span>
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="feedback-empty-state">
                Select a feedback record to review details.
              </div>
            )}
          </aside>
        </div>
      </div>
    </FeedbackManagementPageStyles>
  );
}
